import {
  CORES_ATENDENTE,
  PALETA_ATENDENTES,
  proximaCorLivre,
  type AtendenteDTO,
  type CorAtendente,
  type Papel,
} from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Modal, useToast } from '../../componentes/ui';
import { patch, post } from '../../lib/api';
import { chaves, useAtendentes } from '../../lib/consultas';
import { useCorAtendente, useTemaEscuro } from '../../lib/tema';
import { quandoCurto } from '../../lib/tempo';

export function Atendentes({ eu }: { eu: AtendenteDTO }) {
  const lista = useAtendentes();
  const [editando, setEditando] = useState<AtendenteDTO | 'novo' | null>(null);
  const ativos = (lista.data ?? []).filter((a) => a.ativo);
  const inativos = (lista.data ?? []).filter((a) => !a.ativo);

  return (
    <div className="config-cartao largo">
      <header className="com-acao">
        <div>
          <h2>Atendentes</h2>
          <p>
            Cada atendente tem uma cor: ela aparece nos balões das mensagens e na lista de
            conversas.
          </p>
        </div>
        <button className="btn primario" onClick={() => setEditando('novo')}>
          <UserPlus /> Novo atendente
        </button>
      </header>
      <div className="tabela-wrap">
        <table className="tabela">
          <thead>
            <tr>
              <th>Atendente</th>
              <th>E-mail</th>
              <th>Papel</th>
              <th>Status</th>
              <th>Último acesso</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {[...ativos, ...inativos].map((a) => (
              <Linha key={a.id} a={a} eu={eu} editar={() => setEditando(a)} />
            ))}
          </tbody>
        </table>
      </div>
      {editando && (
        <ModalAtendente
          atual={editando === 'novo' ? null : editando}
          emUso={ativos.map((a) => a.cor)}
          fechar={() => setEditando(null)}
        />
      )}
    </div>
  );
}

function Linha({ a, eu, editar }: { a: AtendenteDTO; eu: AtendenteDTO; editar: () => void }) {
  const cor = useCorAtendente(a.cor);
  return (
    <tr className={a.ativo ? '' : 'inativo'}>
      <td>
        <span className="pessoa">
          <span
            className="avatar"
            style={{
              width: 30,
              height: 30,
              fontSize: 11.5,
              background: cor.fundo,
              color: cor.texto,
            }}
          >
            {a.nomeExibicao.slice(0, 2).toUpperCase()}
          </span>
          <span>
            <b>{a.nomeExibicao}</b>
            {a.id === eu.id && <small> (você)</small>}
            <small className="bloco">{a.nome}</small>
          </span>
        </span>
      </td>
      <td className="mono">{a.email}</td>
      <td>{a.papel === 'admin' ? 'Admin' : 'Atendente'}</td>
      <td>
        {a.ativo ? (
          <span className={`status-txt st-${a.status}`}>{a.status}</span>
        ) : (
          <span className="chip">Desativado</span>
        )}
      </td>
      <td>{a.ultimoAcesso ? quandoCurto(a.ultimoAcesso) : '—'}</td>
      <td>
        <button
          className="icone-btn sem-borda"
          aria-label={`Editar ${a.nomeExibicao}`}
          onClick={editar}
        >
          <Pencil />
        </button>
      </td>
    </tr>
  );
}

function ModalAtendente({
  atual,
  emUso,
  fechar,
}: {
  atual: AtendenteDTO | null;
  emUso: string[];
  fechar: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const escuro = useTemaEscuro();
  const [cor, setCor] = useState<CorAtendente>(atual?.cor ?? proximaCorLivre(emUso));
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const corpo = {
      nome: d.nome,
      nomeExibicao: d.nomeExibicao,
      email: d.email,
      papel: d.papel as Papel,
      cor,
      ...(d.senha ? { senha: d.senha } : {}),
      ...(atual ? { ativo: d.ativo === 'on' } : {}),
    };
    try {
      if (atual) await patch(`/api/atendentes/${atual.id}`, corpo);
      else await post('/api/atendentes', corpo);
      qc.invalidateQueries({ queryKey: chaves.atendentes });
      toast(atual ? 'Atendente atualizado' : 'Atendente cadastrado');
      fechar();
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  return (
    <Modal titulo={atual ? `Editar ${atual.nomeExibicao}` : 'Novo atendente'} aoFechar={fechar}>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="duas">
          <label className="campo">
            <span>Nome completo</span>
            <input id="at-nome" name="nome" className="input" defaultValue={atual?.nome} required />
          </label>
          <label className="campo">
            <span>Nome de exibição</span>
            <input
              id="at-exibicao"
              name="nomeExibicao"
              className="input"
              defaultValue={atual?.nomeExibicao}
              maxLength={24}
              required
            />
          </label>
        </div>
        <label className="campo">
          <span>E-mail</span>
          <input
            id="at-email"
            name="email"
            type="email"
            className="input"
            defaultValue={atual?.email}
            required
          />
        </label>
        <div className="duas">
          <label className="campo">
            <span>{atual ? 'Nova senha (opcional)' : 'Senha inicial'}</span>
            <input
              id="at-senha"
              name="senha"
              type="password"
              className="input"
              minLength={8}
              required={!atual}
              autoComplete="new-password"
            />
          </label>
          <label className="campo">
            <span>Papel</span>
            <select
              id="at-papel"
              name="papel"
              className="input"
              defaultValue={atual?.papel ?? 'atendente'}
            >
              <option value="atendente">Atendente</option>
              <option value="admin">Admin</option>
            </select>
          </label>
        </div>
        <fieldset className="grupo">
          <legend>Cor</legend>
          <div className="cores" role="radiogroup" aria-label="Cor do atendente">
            {CORES_ATENDENTE.map((k) => {
              const p = PALETA_ATENDENTES[k][escuro ? 'escuro' : 'claro'];
              const ocupada = emUso.includes(k) && k !== atual?.cor;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={cor === k}
                  className="cor"
                  title={`${PALETA_ATENDENTES[k].nome}${ocupada ? ' (já em uso)' : ''}`}
                  style={{ background: p.fundo, color: p.texto }}
                  onClick={() => setCor(k)}
                >
                  Aa
                  {ocupada && <span className="cor-uso" aria-hidden />}
                </button>
              );
            })}
          </div>
          <small>
            {PALETA_ATENDENTES[cor].nome}. O ponto marca cores que outro atendente já usa.
          </small>
        </fieldset>
        {atual && (
          <label className="check">
            <input id="at-ativo" name="ativo" type="checkbox" defaultChecked={atual.ativo} /> Ativo
            (desativado não entra no sistema, mas o histórico continua)
          </label>
        )}
        {erro && <div className="erro-form">{erro}</div>}
        <div className="acoes">
          <button type="button" className="btn" onClick={fechar}>
            Cancelar
          </button>
          <button className="btn primario">{atual ? 'Salvar' : 'Cadastrar'}</button>
        </div>
      </form>
    </Modal>
  );
}
